import React from "react";
import { Printer, X } from "lucide-react";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const dashLine = "------------------------------";

export const OrderReceipt = ({ order, onClose }) => {
  if (!order) return null;

  const handlePrint = () => {
    window.print();
  };

  const items = order.items || [];
  const total = Number(order.total_amount || 0);
  const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
  const meals = [order.lunch && "Lunch", order.dinner && "Dinner"].filter(Boolean).join(" + ") || "—";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 print:bg-transparent print:p-0 print:block print:static"
      onClick={onClose}
      data-testid="order-receipt-modal"
    >
      <div
        className="brutal-card w-full max-w-md max-h-[90vh] overflow-y-auto print:max-h-none print:max-w-none print:overflow-visible print:shadow-none print:border-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header bar — hidden on print */}
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4 sticky top-0 bg-white z-10 no-print">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">Receipt Preview</div>
            <div className="font-heading font-semibold text-lg uppercase tracking-tight">Order #{(order.id || "").slice(0, 8)}</div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePrint}
              data-testid="btn-print-receipt"
              className="brutal-btn px-4 py-2 text-xs flex items-center gap-2"
            >
              <Printer size={14} /> Print
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="btn-close-receipt"
              className="brutal-btn-ghost px-3 py-2 text-xs flex items-center"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Printable receipt body */}
        <div className="receipt-paper p-5 font-mono text-[12px] leading-tight print:p-0" data-testid="receipt-body">
          <div className="text-center">
            <div className="font-heading text-xl font-bold tracking-tight uppercase">Dum on the Run<sup className="text-[10px] ml-0.5">®</sup></div>
            <div className="text-[11px] uppercase tracking-widest mt-0.5">DOTR · Order Receipt</div>
          </div>

          <pre className="text-center mt-3 mb-2">{dashLine}</pre>

          <div className="flex justify-between">
            <span>Order ID</span>
            <span className="font-bold">#{(order.id || "").slice(0, 8)}</span>
          </div>
          <div className="flex justify-between">
            <span>Logged</span>
            <span>{formatDateTime(order.created_at)}</span>
          </div>
          <div className="flex justify-between">
            <span>Requested</span>
            <span>{formatDateTime(order.requested_at)}</span>
          </div>
          <div className="flex justify-between">
            <span>Meal</span>
            <span className="font-bold uppercase">{meals}</span>
          </div>

          <pre className="text-center my-2">{dashLine}</pre>

          <div className="text-[11px] uppercase tracking-widest text-center mb-1">Customer</div>
          <div className="font-bold text-base">{order.customer_name}</div>
          {order.phone && <div>Phone: {order.phone}</div>}
          {order.email && <div className="break-all">Email: {order.email}</div>}

          <pre className="text-center my-2">{dashLine}</pre>

          <div className="flex justify-between">
            <span>Mode</span>
            <span className="font-bold uppercase">{order.pickup_delivery}</span>
          </div>
          <div className="flex justify-between">
            <span>Spoke</span>
            <span className="font-bold uppercase">{order.spoke_location}</span>
          </div>

          <pre className="text-center my-2">{dashLine}</pre>

          <div className="text-[11px] uppercase tracking-widest text-center mb-2">Items</div>
          {items.length === 0 ? (
            <div className="text-center italic">No items</div>
          ) : (
            <div className="space-y-1">
              {items.map((it, idx) => {
                const lineTotal = Number(it.line_total ?? (it.unit_price || 0) * (it.quantity || 0));
                return (
                  <div key={idx}>
                    <div className="flex justify-between gap-3">
                      <span className="flex-1">{it.name}</span>
                      <span className="font-bold whitespace-nowrap">£{lineTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[11px] text-gray-700">
                      <span>×{it.quantity} @ £{Number(it.unit_price || 0).toFixed(2)}</span>
                      <span className="uppercase tracking-wider">{it.category || ""}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <pre className="text-center my-2">{dashLine}</pre>

          <div className="flex justify-between">
            <span>Items qty</span>
            <span className="font-bold">{totalQty}</span>
          </div>
          <div className="flex justify-between text-base mt-1">
            <span className="font-bold uppercase">Grand Total</span>
            <span className="font-bold">£{total.toFixed(2)}</span>
          </div>

          {order.special_instructions && (
            <>
              <pre className="text-center my-2">{dashLine}</pre>
              <div className="text-[11px] uppercase tracking-widest mb-1">Notes</div>
              <div className="whitespace-pre-wrap">{order.special_instructions}</div>
            </>
          )}

          <pre className="text-center mt-3">{dashLine}</pre>
          <div className="text-center text-[11px] mt-2">Thank you · See you next run</div>
          <div className="text-center text-[10px] uppercase tracking-widest mt-1">dum on the run</div>

          <pre className="text-center mt-3">{dashLine}</pre>
          <div className="text-center text-[11px] uppercase tracking-widest mb-1 font-bold">
            Bank Details
          </div>
          <div className="text-center text-[12px] font-bold">Esjay Foods Co LTD</div>
          <div className="flex justify-between mt-1">
            <span>Account no.</span>
            <span className="font-bold">30629300</span>
          </div>
          <div className="flex justify-between">
            <span>Sort code</span>
            <span className="font-bold">04-06-05</span>
          </div>
          <pre className="text-center mt-2">{dashLine}</pre>
        </div>
      </div>
    </div>
  );
};
